/**
 * life/src/components/collapsible-list.tsx
 *
 * A deliberate port of dashboard-next's BL-006 collapsible-list. Life is a
 * separate Vite app with its own theme primitives and its own dependency
 * tree — there is no shared component package between the two, and adding
 * one for a 150-line component would be a bigger change than the copy.
 * So the CONTRACT is copied exactly (same exports, same props, same
 * threshold, same tail semantics) and only the styling is re-expressed in
 * Life's idiom (dryvn CSS vars, rounded-full controls, font-mono meta).
 * Behaviour changes must land in both files or the two apps drift.
 *
 * The rule lives here once: past the threshold, an unfiltered list renders
 * its first slice and offers "Show all N". An active search or filter
 * always renders in full — searching IS the expansion, so the two never
 * fight each other.
 *
 * Deliberately NOT in this component: the search input. Every page pins
 * its search ABOVE the list and outside this wrapper, because the input
 * has to survive the loading, error and empty branches — a search box
 * that disappears the moment its own query matches nothing is a box you
 * cannot clear. This component wraps rendering only. It never fetches,
 * never filters, and never owns the query; `filtered` is the page telling
 * it what the page already knows.
 *
 * Collapse state is component state, so it resets on every visit by
 * construction — there is nothing persisted and nothing to clear.
 */

import { useMemo, useState, type ReactNode } from "react";
import { Search } from "lucide-react";

/** Rows shown before a list collapses. */
export const COLLAPSE_THRESHOLD = 8;

export interface CollapsibleListProps<T> {
  items: T[];
  /** Renders the list. Receives the slice that should be on screen. */
  children: (visible: T[]) => ReactNode;
  /** Plural noun for the affordance, e.g. "entries". */
  resource: string;
  /**
   * Row count across every page, where the endpoint reports one. Absent
   * means the loaded count is all that is known — the affordance never
   * quotes a number nothing measured.
   */
  total?: number;
  /** True while a search or filter is on. Suppresses the collapse. */
  filtered?: boolean;
  /** Pagination and similar controls. Hidden while collapsed. */
  footer?: ReactNode;
  threshold?: number;
  /**
   * Collapse from the HEAD instead of the tail: conversation surfaces
   * (Dryvn chat) read oldest-first, so the recent slice is the LAST rows
   * and the affordance ("Show earlier …") sits above.
   */
  tail?: boolean;
  /**
   * Layout for the "Show all" row. Only override where the list sits in a
   * zero-padding surface and the default top margin would put the button
   * flush against the card edge.
   */
  affordanceClassName?: string;
}

export function CollapsibleList<T>({
  items,
  children,
  resource,
  total,
  filtered = false,
  footer,
  threshold = COLLAPSE_THRESHOLD,
  tail = false,
  affordanceClassName = "mt-4 flex flex-wrap items-center gap-3",
}: CollapsibleListProps<T>) {
  const [expanded, setExpanded] = useState(false);

  const collapsed = !filtered && !expanded && items.length > threshold;
  const visible = collapsed
    ? tail
      ? items.slice(items.length - threshold)
      : items.slice(0, threshold)
    : items;

  // `total` counts every row the endpoint holds; `items` is the page in
  // hand. Expanding reveals the page, not the whole table, so when the
  // two differ the affordance says which one it is about.
  const loaded = items.length;
  const knownTotal = total ?? loaded;
  const morePages = knownTotal > loaded;

  const affordance = collapsed ? (
    <div className={affordanceClassName}>
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="rounded-full border px-4 py-1.5 text-xs font-medium transition-colors"
        style={{
          borderColor: "var(--dryvn-line)",
          backgroundColor: "color-mix(in oklab, var(--dryvn-life) 25%, transparent)",
          color: "var(--dryvn-primary-deep)",
        }}
      >
        {tail
          ? `Show earlier ${resource}`
          : morePages
            ? `Show all ${loaded} on this page`
            : `Show all ${knownTotal} ${resource}`}
      </button>
      <span className="font-mono text-[11px]" style={{ color: "var(--dryvn-grey)" }}>
        {tail
          ? `Showing the latest ${threshold} of ${knownTotal}.`
          : morePages
            ? `Showing ${threshold} of ${loaded} on this page — ${knownTotal} ${resource} in total. Search to find the rest.`
            : `Showing ${threshold} of ${knownTotal} — search to find the rest.`}
      </span>
    </div>
  ) : null;

  return (
    <>
      {tail && affordance}
      {children(visible)}
      {tail ? (collapsed ? null : footer) : (affordance ?? footer)}
    </>
  );
}

/**
 * The search input for a list that has none today. Pages with a wired
 * search keep theirs (Vault); this exists so the ones without get the same
 * control rather than a second dialect of it. Styled as the Vault search
 * pill — the one search affordance Life already had.
 */
export function ListSearch({
  value,
  onChange,
  placeholder,
  label,
  className = "mb-4 max-w-sm",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** aria-label — every list search needs a distinct one. */
  label: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <div
        className="flex items-center gap-2 rounded-full border px-4 py-2"
        style={{ borderColor: "var(--dryvn-line)" }}
      >
        <Search className="h-4 w-4 shrink-0" style={{ color: "var(--dryvn-grey)" }} />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label={label}
          className="w-full bg-transparent text-sm outline-none"
          style={{ color: "var(--dryvn-ink)" }}
        />
      </div>
    </div>
  );
}

/**
 * Client-side substring filter over the loaded rows, for the endpoints
 * that take no `search` parameter. Where the server can search, the page
 * keeps sending the query to the server — a client filter over one page
 * would report "no matches" while the match sat on page 2.
 */
// Hook ships with the components it exists for — same file layout the
// dashboard-next original uses, and the same one dryvn.tsx already takes
// for its formatter helper.
// eslint-disable-next-line react-refresh/only-export-components
export function useListFilter<T>(
  items: T[],
  fields: (item: T) => Array<string | null | undefined>,
): { query: string; setQuery: (query: string) => void; results: T[]; active: boolean } {
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();

  const results = useMemo(() => {
    if (needle === "") return items;
    return items.filter((item) =>
      fields(item).some((field) => (field ?? "").toLowerCase().includes(needle)),
    );
    // `fields` is an inline lambda at every call site, so depending on it
    // would rebuild the list on every render. The rows and the needle are
    // what actually change the answer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, needle]);

  return { query, setQuery, results, active: needle !== "" };
}
